import React, { useState, useEffect } from 'react';
import { Mail, RefreshCw, Sparkles } from 'lucide-react';
import AuthLayout from './AuthLayout';
import { supabase } from '../../lib/supabase';

type EmailVerificationProps = {
  email: string;
  onBackToLogin: () => void;
};

export default function EmailVerification({ email, onBackToLogin }: EmailVerificationProps) {
  const [canResend, setCanResend] = useState(false);
  const [countdown, setCountdown] = useState(300); // 5 minutes
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleResendEmail = async () => {
    if (!canResend) return;
    
    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      
      if (error) throw error;
      
      setCanResend(false);
      setCountdown(300);
    } catch (error) {
      console.error('Error resending verification email:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Verifique seu email"
      subtitle="Enviamos um link de confirmação para seu email"
    >
      <div className="space-y-8">
        {/* Email Icon */}
        <div className="flex justify-center">
          <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center relative">
            <Mail className="w-10 h-10 text-blue-500" />
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center animate-bounce">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>

        {/* Email Info */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
            <Mail className="w-4 h-4 text-blue-500" />
            <span className="font-medium text-blue-700">{email}</span>
          </div>
          <p className="text-gray-600">
            Enviamos um email de confirmação para você.<br />
            Clique no link enviado para ativar sua conta e começar a criar conteúdo incrível!
          </p>
        </div>

        {/* Resend Section */}
        <div className="pt-6 border-t">
          <p className="text-sm text-gray-600 mb-4 text-center">
            Não recebeu o email? Você pode reenviar em {formatTime(countdown)}
          </p>
          <button
            onClick={handleResendEmail}
            disabled={!canResend || loading}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-blue-500 rounded-lg text-sm font-medium text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Reenviar email de confirmação
          </button>
        </div>

        {/* Back to Login */}
        <div className="text-center">
          <button
            onClick={onBackToLogin}
            className="text-sm text-blue-600 hover:text-blue-500 font-medium"
          >
            Voltar para o login
          </button>
        </div>
      </div>
    </AuthLayout>
  );
}